import { Hauler, Profile } from '@alliance-disposal/transport-types';
import {
    BanknotesIcon,
    BeakerIcon,
    ClipboardDocumentListIcon,
    ClockIcon,
    Cog6ToothIcon,
    CurrencyDollarIcon,
    MagnifyingGlassIcon,
    MapIcon,
    PresentationChartBarIcon,
    TruckIcon,
    ViewColumnsIcon,
    WrenchIcon,
} from '@heroicons/react/24/solid';

export const ACCESS = {
    customers: ['admin'],
    orders: ['admin'],
    schedules: ['admin'],
    equipment: ['admin'],
    driverTasks: ['admin'],
    // pricing: [
    //   'admin',
    // ],
    portableToilets: ['admin'],
    settings: ['admin'],
    search: ['admin'],
    billing: ['admin'],
    lab: ['admin'],
    appAdmin: ['appAdmin'],
};

export const routes = {
    dashboard: '/',
    auth: {
        login: '/login/',
        groups: 'auth',
    },
    public: {
        updateAdmin: '/update-sourgum/',
        groups: 'public/',
        welcome: '/welcome/',
    },
    orders: {
        list: '/orders/',
        create: '/orders/new/',
        update: (id: string) => `/orders/${id}/`,
        details: (id: string) => `/orders/details/${id}/`,
        groups: ACCESS.orders,
    },

    pricing: {
        list: '/pricing/',
        details: (id: string) => `/pricing/details/${id}/`,
        // create: '/orders/new',
        // update: (id: string) => `/orders/${id}`,
        // details: (id: string) => `/orders/details/${id}`,
        // groups: ACCESS.orders,
    },
    portableToilets: {
        list: '/portable-toilets/',
        create: '/portable-toilets/new/',
        ptDispatch: '/portable-toilets/pt-dispatch/',
        update: (subscriptionId: string): string => `/portable-toilets/edit/${subscriptionId}/`,
        details: (subscriptionId: string): string => `/portable-toilets/details/${subscriptionId}/`,
        groups: ACCESS.portableToilets,
    },
    driverTracking: {
        list: '/driver-tracking/',
    },
    timeSheet: {
        list: '/time-sheet/',
    },

    settings: {
        list: '/settings/',
        generalSettings: '/settings/#general-settings',
        billingSettings: '/settings/#billing-settings',
        billing: {
            onboard: '/settings/billing/start',
            dashboard: '/settings/billing/dashboard',
        },
        listUsers: '/settings/#users',
        accountingIntegrations: '/settings/accounting-integrations/',
        quickBooksOnlineMapping: '/settings/quickbooks-online/mapping/',
        quickBooksOnlineAuthCallback: '/settings/quickbooks-online/auth-callback/',
        quickBooksDesktopSettings: '/settings/quickbooks-desktop/',
        quickBooksDesktopDownload: '/settings/quickbooks-desktop-download/',
        groups: ACCESS.settings,
    },
    roScheduling: {
        list: '/ro-schedule/',
    },
    dispatch: {
        list: '/dispatch/',
    },
    // pricing: {
    //   list: '/pricing',
    //   create: '/pricing/new',
    //   update: (id) => `/pricing/${id}`,
    //   groups: ACCESS.pricing
    // },
    equipment: {
        map: '/equipment/map/',
        inventory: '/equipment/inventory',
        vehicles: '/equipment/vehicles',
        containers: '/equipment/containers',
    },
    subscriptions: {
        create: '/subscriptions/new/',
        list: '/subscriptions/',
        details: (id: string) => `/subscriptions/details/${id}/`,
        editEquipment: (id: string) => `/subscriptions/edit-equipment/${id}/`,
    },
    dispatching: {
        dashboard: '/dispatching/',
        routing: (params?: { routeIDs?: string[]; selectedRouteID?: string; date?: string }) => {
            const searchParams = new URLSearchParams();
            if (params?.routeIDs) {
                searchParams.append('routeIDs', params.routeIDs.join(','));
                if (params?.selectedRouteID) {
                    searchParams.append('selectedRouteID', params.selectedRouteID);
                } else {
                    searchParams.append('selectedRouteID', params.routeIDs[0]);
                }
            }
            if (params?.date) searchParams.append('date', params.date);
            return `/dispatching/routing/${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
        },
        containerDelivery: '/dispatching/container-delivery/',
        schedule: '/dispatching/schedule/',
        commercialRoutes: {
            list: '/dispatching/commercial-routes/',
            details: (id: string) => `/dispatching/commercial-routes/details/${id}/`,
        },
        reschedule: '/dispatching/reschedule/',
        events: '/dispatching/events/',
    },

    rollOffDumpsters: {
        list: '/equipment/',
        create: '/roll-off-dumpsters/new/',
        update: (id: string) => `/roll-off-dumpsters/${id}/`,
        details: (id: string) => `/roll-off-dumpsters/details/${id}/`,
        groups: ACCESS.equipment,
    },
    customers: {
        list: '/customers',
        // create: '/customers/new',
        update: (id: string) => `/customers/${id}/`,
        details: (id: string) => `/customers/details/${id}/`,
        groups: ACCESS.customers,
    },
    schedules: {
        list: '/schedules/',
        groups: ACCESS.schedules,
    },
    users: {
        update: (id: string) => `/users/${id}/`,
        list: '/settings/#users',
    },
    reports: {
        list: '/reports',
        taxLiability: '/reports/sales-tax-liability',
        paymentsBreakdown: '/reports/payments-breakdown',
        rollOffDeliveries: '/reports/roll-off-deliveries',
        rollOffSalesRevenue: '/reports/roll-off-sales-revenue',
        rollOffBilling: '/reports/roll-off-billing',
        rollOffOperations: '/reports/roll-off-operations',
        rollOffOnlineOrders: '/reports/roll-off-online-orders',
    },
    search: {
        list: '/search/',
        groups: ACCESS.search,
    },
    billing: {
        list: '/billing/',
        rollOffDetails: (rolloff: string) => `/billing/?rolloff=${rolloff}`,
        commercialSubscriptionDetails: (id: string, billingActivityId?: string) =>
            `/billing/?subscription=${id}${billingActivityId ? `&billingActivity=${billingActivityId}` : ''}`,
        operationsSubscriptionDetails: (id: string, billingPeriodID?: string, ordRoID?: string) =>
            `/billing/?subscription=${id}${billingPeriodID ? `&billingPeriodID=${billingPeriodID}` : ''}${ordRoID ? `&ordRo=${ordRoID}` : ''}`,
        // Context resolves the parent subscription from the ord_ro id, so the URL needs only ordRo.
        rollOffOrderRoDetails: (ordRoID: string) => `/billing/?ordRo=${ordRoID}`,
        combineInvoices: '/billing/combine-invoices/',
        groups: ACCESS.billing,
    },
    requestForProposal: {
        rfpsList: '/rfps',
    },
    partnerPortalConnect: {
        list: '/partner-portal-connect/',
        payouts: '/partner-portal-connect/payouts/',
        plaidOAuth: '/partner-portal-connect/plaid-oauth/',
    },
    lab: {
        list: '/lab/',
        groups: ACCESS.lab,
    },
    appAdmin: {
        list: '/app-admin/',
        audit: '/app-admin/audit-log/',
        customerImport: '/app-admin/customer-import/',
        groups: ACCESS.appAdmin,
    },
    liftline: {
        list: '/liftline/',
    },
    billingV2: {
        invoices: '/billing-v2/invoices/',
        invoiceDetails: (invoiceId: string) => `/billing-v2/invoices/${invoiceId}/`,
    },
    taskBoard: '/task-board/',
    devTest: '/dev-test/',
    operationsRollOff: {
        // All paths except details - which has more path params than legacy details - are the same as legacy rolloff,
        // assuming new and legacy are mutually exclusive
        orders: '/orders/',
        schedules: '/ro-schedule/',
        details: (subscriptionId: string, ordId: string, ordRoId: string) =>
            `/operations/subscriptions/${subscriptionId}/order/${ordId}/order_ro/${ordRoId}/`,
        create: '/orders/new/',
    },
    commercialPreview: '/commercial-preview/',
    scheduleWaysteDemo: 'https://meetings.hubspot.com/dan-densen/wayste-demo-rr?uuid=92e1fadb-b707-45c7-a0b9-fcb52a12970a',
    scheduleCommercialDemo: 'https://meetings.hubspot.com/julia-london2/expand',
};

export default routes;

export function getRfpListPath(): string {
    return routes.requestForProposal.rfpsList;
}

export const routeList = (user: Profile.ProfileTransport, userHaulerInfo: Hauler.HaulerTransport, isAppAdmin = false) => {
    return [
        {
            text: 'Dashboard',
            path: routes.dashboard,
            exact: true,
            icon: <PresentationChartBarIcon />,
            disabled: false,
        },
        {
            text: 'Orders',
            path: routes.orders.list,
            exact: false,
            icon: <ClipboardDocumentListIcon />,
            groups: ACCESS.orders,
            disabled: false,
        },
        {
            text: 'Schedules',
            path: routes.roScheduling.list,
            exact: false,
            icon: <ViewColumnsIcon />,
            groups: ACCESS.schedules,
            disabled: userHaulerInfo.waysteLite,
        },
        {
            text: 'Billing',
            path: routes.billing.list,
            exact: false,
            icon: <BanknotesIcon />,
            groups: ACCESS.billing,
            disabled: false,
        },
        {
            text: 'Equipment',
            path: routes.rollOffDumpsters.list,
            exact: false,
            icon: <TruckIcon />,
            disabled: userHaulerInfo.waysteLite,
        },

        {
            text: 'Pricing',
            path: routes.pricing.list,
            exact: false,
            icon: <CurrencyDollarIcon />,
            disabled: false,
        },
        {
            text: 'Portable Toilets',
            path: routes.portableToilets.list,
            exact: false,
            icon: <ClipboardDocumentListIcon />,
            groups: ACCESS.portableToilets,
            disabled: false,
        },
        {
            text: 'Tracking',
            path: routes.driverTracking.list,
            exact: false,
            icon: <MapIcon />,
            disabled: userHaulerInfo.waysteLite,
        },
        {
            text: 'Time Sheet',
            path: routes.timeSheet.list,
            exact: false,
            icon: <ClockIcon />,
            disabled: userHaulerInfo.waysteLite,
        },
        {
            text: 'Search',
            path: routes.search.list,
            exact: false,
            icon: <MagnifyingGlassIcon />,
            disabled: false,
        },
        {
            text: 'Settings',
            path: routes.settings.list,
            exact: false,
            icon: <Cog6ToothIcon />,
            groups: ACCESS.schedules,
            disabled: false,
        },
        {
            text: 'Admin',
            path: routes.appAdmin.list,
            exact: false,
            icon: <WrenchIcon />,
            groups: ACCESS.appAdmin,
            hide: !isAppAdmin,
        },
        {
            text: 'Lab',
            path: routes.lab.list,
            exact: false,
            icon: <BeakerIcon />,
            groups: ACCESS.lab,
            hide: !isAppAdmin,
        },
    ].filter((route) => !route.disabled);
};
